import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';
import { RootState } from 'state/store';

import { useSystemTranslation } from 'hooks';
import { AddonOption } from 'types/addon';
import { formatCurrency } from 'utils/format';

interface Props {
  modalTitle: string;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  elementList: AddonOption[];
  selectedAddons: AddonOption[];
  handleSave: (addOns: AddonOption[]) => void;
  isApplicationLocked: boolean;
}
export function AddonModal(props: Props) {
  const {
    elementList,
    isOpen,
    modalTitle,
    onClose,
    title,
    selectedAddons = [],
    handleSave,
    isApplicationLocked,
  } = props;

  const [addOns, setAddOn] = useState<AddonOption[]>([...selectedAddons]);

  const { unit } = useSelector((state: RootState) => state.unitSelection);
  const { tSystem } = useSystemTranslation();

  function handleAddOnSelection(e: React.ChangeEvent<HTMLInputElement>, element: AddonOption) {
    const isSelected = e.target.checked;

    if (isSelected) {
      setAddOn((prevState) => [...prevState, element]);
    } else {
      setAddOn((prevState) => prevState.filter((i) => i.id !== element.id));
    }
  }

  useEffect(() => {
    setAddOn(selectedAddons);
  }, [selectedAddons]);

  function handleSaveAddOn() {
    handleSave(addOns);
  }

  const selectedIds = addOns.map((i) => i.id);
  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="black.200" backdropFilter="blur(12px) hue-rotate(0deg)" />
      <ModalContent background="white.base" borderRadius="8px">
        <ModalHeader
          background="blue.base"
          color="white.base"
          p={2}
          fontSize={16}
          borderTopLeftRadius="8px"
          borderTopRightRadius="8px"
        >
          {modalTitle}
        </ModalHeader>
        <ModalCloseButton
          aria-label={tSystem(SystemTranslationKeys.common.close)}
          color="white.base"
        />
        <ModalBody minHeight={100}>
          <Box mt={5}>
            <Heading as="h5" size="sm">
              {title ?? ''}
            </Heading>
            <Box mt={5}>
              {elementList.map((element) => {
                const isFixedUnitAddOn = element.unitSpaceId !== null && !element.isRented;

                if (isFixedUnitAddOn && !unit) return null;

                return (
                  <Flex key={`element-list-${element.id}`} justifyContent="space-between" my={2}>
                    {isFixedUnitAddOn ? (
                      <Checkbox size="sm" isChecked isDisabled={isApplicationLocked}>
                        {element.name}
                      </Checkbox>
                    ) : (
                      <Checkbox
                        size="sm"
                        isDisabled={isApplicationLocked}
                        defaultChecked={element.isRented || selectedIds.includes(element.id)}
                        onChange={(e) => handleAddOnSelection(e, element)}
                      >
                        {element.name}
                      </Checkbox>
                    )}
                    <Text fontSize="sm">
                      {element?.isRented && (
                        <Badge colorScheme="red">
                          {tSystem(SystemTranslationKeys.common.rented)}
                        </Badge>
                      )}
                      {!element?.isRented &&
                        element.rates.length > 0 &&
                        `${formatCurrency(Number(element.rates[0].charge))} / ${
                          element.rates[0].frequency
                        }`}
                      {!element?.isRented &&
                        element.rates.length === 0 &&
                        tSystem(SystemTranslationKeys.common.free)}
                    </Text>
                  </Flex>
                );
              })}
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Button size="sm" bg="silver.highlighthalf" color="black.dark" onClick={onClose}>
            {tSystem(SystemTranslationKeys.common.cancel)}
          </Button>
          <Button
            size="sm"
            variant="brand"
            onClick={handleSaveAddOn}
            isDisabled={isApplicationLocked}
          >
            {tSystem(SystemTranslationKeys.common.save)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
